// src/features/links/SourceGroupsCell.tsx
// IR-1047 — Source Groups cell for the Join Links table (CAT-03).
// Renders up to MAX_VISIBLE group-name chips inline; if there are more, a
// "+K" trigger opens a Radix tooltip listing every source group's name.
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { highlightMatches } from "./highlight";
import type { SourceGroupRef } from "./types";

const MAX_VISIBLE = 2;

/**
 * The exact string this cell renders for a source group. Exported because the
 * IR-1233 r6 auto-reveal decision (column-visibility) must ask "would this row
 * show a highlighted chip?" against the SAME string the chip renders — the
 * same lesson as r4's match-first partition: derive once, never re-derive.
 */
export function sourceGroupLabel(g: SourceGroupRef): string {
  return g.display_name ?? `#${g.entity_id}`;
}

interface Props {
  groups: SourceGroupRef[];
  /** IR-1233 r3 — active search query; occurrences in chip names get marked. */
  highlightQuery?: string | undefined;
}

function chip(g: SourceGroupRef, idx: number, highlightQuery?: string) {
  return (
    <span
      key={`${g.entity_id}-${idx}`}
      className="inline-flex max-w-[14ch] items-center truncate rounded-full bg-[var(--iris-bg-chip)] px-2 py-0.5 text-xs text-[var(--iris-fg-3)]"
      title={g.display_name ?? String(g.entity_id)}
    >
      {highlightMatches(sourceGroupLabel(g), highlightQuery)}
    </span>
  );
}

export function SourceGroupsCell({ groups, highlightQuery }: Props) {
  if (!groups || groups.length === 0) {
    return <span className="text-[var(--iris-fg-4)]">—</span>;
  }

  // IR-1233 r4: matched chips fill the visible slots first, so a search hit on
  // the 3rd+ group is never hidden behind "+N" (the hit counted in match nav
  // but had no visible mark). Partition on the RENDERED name — the same string
  // handed to highlightMatches — so partition and marking can never disagree.
  // Both partitions keep their original relative order (filter is stable); with
  // no query or no matches the order is byte-identical to the plain slice.
  const needle = highlightQuery?.trim().toLowerCase();
  const isMatch = (g: SourceGroupRef) =>
    !!needle && sourceGroupLabel(g).toLowerCase().includes(needle);
  const ordered = needle
    ? [...groups.filter(isMatch), ...groups.filter((g) => !isMatch(g))]
    : groups;

  const visible = ordered.slice(0, MAX_VISIBLE);
  const overflow = groups.length - visible.length;

  return (
    <div className="flex flex-wrap items-center gap-1">
      {visible.map((g, i) => chip(g, i, highlightQuery))}
      {overflow > 0 && (
        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={`Show all ${groups.length} source groups`}
                className="inline-flex items-center rounded-full bg-[var(--iris-bg-4)] px-2 py-0.5 text-xs text-[var(--iris-fg-2)] hover:text-[var(--iris-fg-1)]"
              >
                +{overflow}
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-[260px]">
              <ul className="flex flex-col gap-0.5">
                {groups.map((g, i) => (
                  <li key={`${g.entity_id}-all-${i}`} className="truncate">
                    {highlightMatches(sourceGroupLabel(g), highlightQuery)}
                  </li>
                ))}
              </ul>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}
